import React from "react";
import styled from "styled-components";
import { buildTypographyInPixels } from "./helpers";
import { ITypography, TColor } from "../../types";

interface IStyledProps {
    $color?: TColor;
    styles?: React.CSSProperties;
    variant: keyof ITypography;
}

const StyledTypography = styled.p<IStyledProps>(({ theme, variant, styles, $color }) => ({
    ...buildTypographyInPixels(theme.v2.typography[variant]),
    color: $color ? theme.v2.colors[$color] : "inherit",
    margin: 0,
    ...styles
}));

type THeadings = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type TText = "p" | "span";
type TAnchor = "a";
type TLabel = "label";
type TDiv = "div";
type TOtherText =
    | "abbr"
    | "address"
    | "b"
    | "blockquote"
    | "caption"
    | "cite"
    | "code"
    | "del"
    | "dfn"
    | "em"
    | "figcaption"
    | "i"
    | "ins"
    | "kbd"
    | "legend"
    | "mark"
    | "pre"
    | "q"
    | "s"
    | "samp"
    | "small"
    | "strong"
    | "sub"
    | "summary"
    | "sup"
    | "u"
    | "var";

type TAvailableTags = THeadings | TText | TAnchor | TLabel | TDiv | TOtherText;

type TTypographyProps<T extends TAvailableTags | undefined> = {
    as?: T;
    children: React.ReactNode;
    variant?: keyof ITypography;
    styles?: React.CSSProperties;
    color?: TColor;
} & JSX.IntrinsicElements[T extends undefined ? "p" : T];

const Typography = <T extends TAvailableTags | undefined>({
    as,
    variant = "paragraph-medium-regular",
    children,
    color,
    styles = {},
    ...props
}: TTypographyProps<T>) => {
    return (
        <StyledTypography as={as} variant={variant} $color={color} styles={styles} {...props}>
            {children}
        </StyledTypography>
    );
};

export default Typography;
